import React, { Component } from 'react';
import { inject, observer } from 'mobx-react';
import autoBindMethods from 'class-autobind-decorator';
import Helmet from 'react-helmet';

import { Col, Grid, Nav, Row } from 'react-bootstrap';

import ClientsClass from '../../clients/ClientsClass';
import { AppConstants } from '../../constants';
import { ILoanAccount } from '../../interfaces';
import { LoanAccountActivityStore, SessionStoreClass } from '../../stores';
import { Link } from '../common';

import { ILoanAccountFormData } from './leadSource';
import LoanAccountHeaderOwners from './LoanAccountHeaderOwners';
import LoanAccountTabNavLink from './LoanAccountTabNavLink';
import TabCompliance from './main-compliance/TabCompliance';
import TabContacts from './main-contacts/TabContacts';
import TabDetails from './main-details/TabDetails';
import TabDocuments from './main-documents/TabDocuments';
import TabLoans from './main-loans/TabLoans';
import TabPayments from './main-payments/TabPayments';
import TabPrimaryCases from './main-primary-cases/TabPrimaryCases';
import SidebarFeed from './sidebar-feed/SidebarFeed';

const MAIN_COL_WIDTHS = { lg: 8, xs: 9 }
  , ASIDE_COL_WIDTHS = { lg: 4, xs: 3 }
  ;

const {
  LOAN_ACCOUNT_ACTIVITY_TYPES,
  LOAN_ACCOUNT_MAIN_TABS,
  LOAN_ACCOUNT_TYPES,
  PERMISSIONS,
  ROUTING,
} = AppConstants;

interface IMainTab {
  // A tab only some accounts have anything behind: collateral cases are the law firm's own case
  // list, and a medical account has none to show
  accountType?: string;
  key: string;
  label: string;
  permissions?: string[];
}

// The lending routes are gated on VIEW_LOAN_ACCOUNT alone, so a tab needing more than that says so
// here. Compliance reads charges and payments, and without both it can only render two error toasts
// and an empty table that claims the account has no charges.
const MAIN_TABS: IMainTab[] = [
  { key: LOAN_ACCOUNT_MAIN_TABS.DETAILS, label: 'Details' },
  { key: LOAN_ACCOUNT_MAIN_TABS.LOANS, label: 'Loans' },
  {
    key: LOAN_ACCOUNT_MAIN_TABS.PAYMENTS,
    label: 'Payments',
    permissions: [PERMISSIONS.VIEW_LOAN_PAYMENT],
  },
  {
    key: LOAN_ACCOUNT_MAIN_TABS.COMPLIANCE,
    label: 'Compliance',
    permissions: [PERMISSIONS.VIEW_LOAN_COMPLIANCE_CHARGE, PERMISSIONS.VIEW_LOAN_PAYMENT],
  },
  {
    accountType: LOAN_ACCOUNT_TYPES.LAW_FIRM,
    key: LOAN_ACCOUNT_MAIN_TABS.PRIMARY_CASES,
    label: 'Primary cases',
    // Reads the cases and their updates, so without both it can only show a case whose feed is an
    // error toast
    permissions: [PERMISSIONS.VIEW_LOAN_COLLATERAL_CASE, PERMISSIONS.VIEW_LOAN_COLLATERAL_CASE_UPDATE],
  },
  {
    key: LOAN_ACCOUNT_MAIN_TABS.DOCUMENTS,
    label: 'Documents',
    permissions: [PERMISSIONS.VIEW_LOAN_ACCOUNT_DOCUMENT],
  },
  {
    key: LOAN_ACCOUNT_MAIN_TABS.CONTACTS,
    label: 'Contacts',
    permissions: [PERMISSIONS.VIEW_LOAN_ACCOUNT_CONTACT],
  },
];

interface IProps {
  loanAccount: ILoanAccount;
  main: string;
  onSave: (data: ILoanAccountFormData) => Promise<void>;
}

interface IInjected extends IProps {
  Clients: ClientsClass;
  SessionStore: SessionStoreClass;
}

@inject('Clients', 'SessionStore')
@autoBindMethods
@observer
class LoanAccount extends Component<IProps> {
  // Held here rather than in SidebarFeed so that a tab can refresh the feed after writing to it.
  // Scoped to this account for the life of the component: LoanAccountPage keys the page on the
  // account id, so a different account gets a new store rather than reusing this one's params.
  private activityStore: LoanAccountActivityStore;

  constructor (props: IProps) {
    super(props);

    this.activityStore = new LoanAccountActivityStore(
      (props as IInjected).Clients.loanAccountActivities,
      { account: props.loanAccount.id },
    );
  }

  public componentDidMount () {
    this.activityStore.loadInitial();
  }

  private get injected () {
    return this.props as IInjected;
  }

  // Assigning an owner writes an OWNER_ASSIGNED entry, so the feed is re-read for that type
  private refreshForOwners () {
    return this.activityStore.refreshForType(LOAN_ACCOUNT_ACTIVITY_TYPES.OWNER_ASSIGNED);
  }

  // A tab with no `permissions` is open to anyone the lending routes already let through, and one
  // with no `accountType` belongs on every account.
  private get visibleTabs () {
    const { loanAccount } = this.props
      , { SessionStore } = this.injected
      ;

    return MAIN_TABS.filter(tab =>
      (!tab.accountType || tab.accountType === loanAccount.type)
      && (tab.permissions || []).every(permission => SessionStore.userHasPermission(permission)));
  }

  private isTabVisible (key: string) {
    return this.visibleTabs.some(tab => tab.key === key);
  }

  public renderNav () {
    const { loanAccount, main } = this.props;

    return (
      <Grid fluid>
        <Nav bsStyle='tabs' activeKey={main}>
          {this.visibleTabs.map(tab => (
            <LoanAccountTabNavLink
              key={tab.key}
              label={tab.label}
              loanAccountId={loanAccount.id}
              toTab={tab.key}
            />
          ))}
        </Nav>
      </Grid>
    );
  }

  // Keyed rather than a chain of ifs, which grew past the complexity threshold once there were
  // seven tabs. Only the tabs that write to the activity feed pass a callback refreshing it.
  private get tabRenderers (): { [key: string]: () => JSX.Element } {
    const { loanAccount, onSave } = this.props
      , { refresh } = this.activityStore
      ;

    return {
      [LOAN_ACCOUNT_MAIN_TABS.DETAILS]: () => <TabDetails loanAccount={loanAccount} onSave={onSave} />,
      [LOAN_ACCOUNT_MAIN_TABS.LOANS]: () => <TabLoans loanAccount={loanAccount} onLoanChanged={refresh} />,
      [LOAN_ACCOUNT_MAIN_TABS.PAYMENTS]: () => <TabPayments loanAccount={loanAccount} onPaymentLogged={refresh} />,
      [LOAN_ACCOUNT_MAIN_TABS.COMPLIANCE]: () => <TabCompliance loanAccount={loanAccount} onPaymentLogged={refresh} />,
      [LOAN_ACCOUNT_MAIN_TABS.PRIMARY_CASES]: () => <TabPrimaryCases loanAccount={loanAccount} />,
      [LOAN_ACCOUNT_MAIN_TABS.DOCUMENTS]: () => <TabDocuments loanAccount={loanAccount} />,
      [LOAN_ACCOUNT_MAIN_TABS.CONTACTS]: () => <TabContacts loanAccount={loanAccount} />,
    };
  }

  public renderTab () {
    const { main } = this.props
      , renderer = this.tabRenderers[main]
      ;

    // Checked against the visible tabs rather than the full list, so that typing the URL of a tab
    // the nav is hiding lands on the same message an unknown tab does rather than on a broken one.
    if (!this.isTabVisible(main) || !renderer) {
      return this.renderMissingTab();
    }

    return renderer();
  }

  private renderMissingTab () {
    const { loanAccount, main } = this.props;

    return (
      <div className='empty'>
        <p>The <b>{main}</b> tab does not exist.</p>
        <p>
          <Link to={`${ROUTING.loanAccounts}/${loanAccount.id}/${LOAN_ACCOUNT_MAIN_TABS.DETAILS}`}>
            Go to Account Details
          </Link>
        </p>
      </div>
    );
  }

  public render () {
    const { loanAccount, onSave } = this.props;

    return (
      <div className='loan-account-content'>
        <Helmet title={`Account - ${loanAccount.name}`} />
        <header>
          <div className='col-title'>
            <h1>{loanAccount.name}</h1>
          </div>
          <div className='col-right'>
            <LoanAccountHeaderOwners
              loanAccount={loanAccount}
              onOwnerChanged={this.refreshForOwners}
              onSave={onSave}
            />
          </div>
        </header>
        <Row className='main'>
          <Col className='col-main' {...MAIN_COL_WIDTHS}>
            {this.renderNav()}
            {this.renderTab()}
          </Col>
          <Col className='col-aside' {...ASIDE_COL_WIDTHS}>
            <SidebarFeed activityStore={this.activityStore} loanAccount={loanAccount} />
          </Col>
        </Row>
      </div>
    );
  }
}

export default LoanAccount;
